import { AxiosError } from 'axios'
import { ENDPOINT_PROJECT_STATUSES } from '../config'
import type { ProjectStatusMembershipResponse } from '../types'
import AuthAxios from '@/lib/api/axios/AuthAxios'

/**
 * Detach a workspace status from a specific project, hiding it from that
 * project's status picker.
 *
 * Backend: `DELETE /api/v1.0/e/{workspaceSlug}/projects/{projectId}/statuses/{statusId}`
 *
 * The server handles three cases atomically:
 * - If the status is currently global, the server expands its association set
 *   to "every workspace project except this one" in a single transaction. The
 *   response includes `expandedToProjects` with the count of projects the
 *   status was explicitly scoped to as a result.
 * - If the status is already explicitly scoped and this project is in the set,
 *   the row is soft-deleted.
 * - If this would leave the status with zero associations, the server returns 400
 *   with a message instructing the caller to delete the status from the
 *   workspace-level management page instead.
 *
 * System-generated statuses (BL/CD) cannot be detached — they are always global.
 */
const detachStatusFromProject = async (
  workspaceSlug: string,
  projectId: string,
  statusId: string,
): Promise<ProjectStatusMembershipResponse> => {
  try {
    const response = await AuthAxios.delete<ProjectStatusMembershipResponse>(
      `${ENDPOINT_PROJECT_STATUSES}/${workspaceSlug}/projects/${projectId}/statuses/${statusId}`,
    )
    return response.data
  } catch (error) {
    if (error instanceof AxiosError) {
      if (error.response?.status === 403) {
        throw new Error('You do not have permission to manage statuses for this project')
      }
      if (error.response?.status === 400 && typeof error.response.data?.message === 'string') {
        throw new Error(error.response.data.message)
      }
    }
    throw error
  }
}

export default detachStatusFromProject
